import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ShareButton from '../components/ui/ShareButton';
import './PublicContent.css';

const ESTADOS = {
  reportado: { lbl: '📢 Reportado', cls: 'badge-gold' },
  rescate: { lbl: '🦺 Rescatado', cls: 'badge-blue' },
  tratamiento: { lbl: '🏥 Tratamiento', cls: 'badge-red' },
  adopcion: { lbl: '🏠 En adopción', cls: 'badge-green' },
  adoptado: { lbl: '🎒 Adoptado', cls: 'badge-green' }
};

const TIPO_EVENTO = {
  rescate: 'Rescate',
  adopcion: 'Adopción',
  vacunacion: 'Vacunación',
  alimentacion: 'Alimentación',
  capacitacion: 'Capacitación',
  otro: 'Actividad'
};

export default function PublicContent({ tipo }) {
  const { publicId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    setData(null);

    const pathByTipo = {
      mascota: `/publico/mascotas/${publicId}`,
      actividad: `/publico/actividades/${publicId}`,
      campana: `/publico/campanas/${publicId}`,
      urgente: `/publico/urgentes/${publicId}`
    };
    const path = pathByTipo[tipo];
    if (!path) {
      setNotFound(true);
      setLoading(false);
      return undefined;
    }

    api.get(path)
      .then(r => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setNotFound(true); })
      .finally(() => { if (!cancel) setLoading(false); });

    return () => { cancel = true; };
  }, [tipo, publicId]);

  if (loading) {
    return (
      <div className="page-wrap fade-up">
        <div className="spinner" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="page-wrap fade-up pub-page">
        <div className="card pub-missing">
          <div className="pub-missing-ico">🔍</div>
          <h1 className="pub-missing-title">Contenido no disponible</h1>
          <p className="pub-missing-msg">
            Este contenido no existe, fue retirado o todavía no es público.
          </p>
          <div className="pub-actions">
            <button className="btn btn-v" onClick={() => nav('/')}>Ir al inicio</button>
          </div>
        </div>
      </div>
    );
  }

  const shareTitle = (() => {
    if (tipo === 'mascota') return `${data.nombre} — Red Cimarrona`;
    if (tipo === 'campana') return `${data.titulo} — Red Cimarrona`;
    if (tipo === 'urgente' && data.esUrgente) return `🚨 Urgente: ${data.nombre} — Red Cimarrona`;
    return `${data.nombre || data.titulo} — Red Cimarrona`;
  })();

  const shareText = (() => {
    if (tipo === 'mascota') return `Conoce a ${data.nombre} en Red Cimarrona`;
    if (tipo === 'campana') return `Apoya: ${data.titulo}`;
    return `Actividad de Red Cimarrona: ${data.nombre}`;
  })();

  return (
    <div className="page-wrap fade-up pub-page">
      <div className="pub-crumb">
        <button className="btn-link" onClick={() => nav('/')}>Inicio</button>
        <span className="pub-crumb-sep">/</span>
        <span>
          {tipo === 'mascota' && 'Mascota'}
          {tipo === 'actividad' && 'Actividad'}
          {tipo === 'campana' && 'Campaña'}
          {tipo === 'urgente' && 'Actividad urgente'}
        </span>
      </div>

      <div className="card pub-card">
        {tipo === 'mascota' && (
          <>
            <div className="pub-hero" style={{ background: data.colorBg || '#d1fae5' }}>
              {data.fotos && data.fotos[0] ? (
                <img src={data.fotos[0]} alt={data.nombre} className="pub-img" />
              ) : (
                <span className="pub-emoji">{data.icono || '🐾'}</span>
              )}
              <span className={`badge ${ESTADOS[data.estado]?.cls || 'badge-green'} pub-status`}>
                {ESTADOS[data.estado]?.lbl || data.estado}
              </span>
            </div>
            <div className="pub-body">
              <div className="pub-head">
                <div>
                  <div className="pub-kicker">Mascota</div>
                  <h1 className="pub-title">{data.nombre}</h1>
                  <div className="pub-meta">
                    {data.especie} · {data.edad}
                    {data.tamano ? ` · ${data.tamano}` : ''}
                    {data.sexo && data.sexo !== 'desconocido' ? ` · ${data.sexo}` : ''}
                  </div>
                </div>
                <ShareButton
                  tipo="mascota"
                  publicId={data.publicId}
                  title={shareTitle}
                  text={shareText}
                  data={data}
                  className="pub-share"
                />
              </div>

              <div className="pub-chips">
                {data.ubicacion && <span className="pub-chip">📍 {data.ubicacion}</span>}
                {data.vacunas != null && (
                  <span className="pub-chip">{data.vacunas ? '💉 Vacunado' : '💉 Sin vacunas registradas'}</span>
                )}
                {data.estado === 'adoptado' && (
                  <span className="pub-chip pub-chip-ok">✅ Ya encontró hogar</span>
                )}
                {data.estado === 'adopcion' && (
                  <span className="pub-chip pub-chip-go">🏠 Disponible para adopción</span>
                )}
              </div>

              {data.historia && (
                <section className="pub-section">
                  <h2 className="pub-h2">Historia</h2>
                  <p className="pub-p">{data.historia}</p>
                </section>
              )}
              {data.salud && (
                <section className="pub-section">
                  <h2 className="pub-h2">Salud</h2>
                  <p className="pub-p">{data.salud}</p>
                </section>
              )}

              <div className="pub-actions">
                <button className="btn btn-v" onClick={() => nav('/animales')}>
                  Ver más mascotas
                </button>
                {data.estado === 'adopcion' && (
                  <button className="btn btn-outline" onClick={() => nav('/animales')}>
                    Quiero adoptar
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {(tipo === 'actividad' || tipo === 'urgente') && (
          <>
            <div className={`pub-hero pub-hero-plain ${tipo === 'urgente' && data.esUrgente ? 'pub-hero-urgent' : ''}`}>
              {tipo === 'urgente' && data.esUrgente && (
                <div className="pub-urgent-banner">🚨 ACTIVIDAD URGENTE</div>
              )}
              <div className="pub-kicker">
                {tipo === 'urgente' ? 'Actividad urgente' : 'Actividad'}
                {data.tipo ? ` · ${TIPO_EVENTO[data.tipo] || data.tipo}` : ''}
              </div>
              <h1 className="pub-title">{data.nombre}</h1>
              <div className="pub-meta">
                {data.fecha ? new Date(data.fecha).toLocaleDateString('es-MX', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                }) : ''}
              </div>
              {data.imagen && (
                <img src={data.imagen} alt={data.nombre} className="pub-act-img" />
              )}
              <div className="pub-chips" style={{ marginTop: 12 }}>
                <span className="pub-chip">
                  {data.modalidad === 'en_vivo' ? '🌐 En vivo' : '📍 Presencial'}
                </span>
                {data.horaInicio && (
                  <span className="pub-chip">
                    🕐 {data.horaFin ? `${data.horaInicio} – ${data.horaFin}` : data.horaInicio}
                  </span>
                )}
                {data.ubicacion && <span className="pub-chip">📍 {data.ubicacion}</span>}
                {data.esDePago ? (
                  <span className="pub-chip">💰 ${data.precio}</span>
                ) : (
                  <span className="pub-chip">✨ Gratis</span>
                )}
              </div>
            </div>
            <div className="pub-body">
              <div className="pub-head">
                <div className="pub-meta-inline">
                  <span className="pub-stat">
                    👥 {data.voluntariosInscritos ?? 0} / {data.voluntariosNecesarios} voluntarios
                  </span>
                  {data.metaFondos > 0 && (
                    <span className="pub-stat">
                      💰 ${(data.fondosActuales || 0).toLocaleString()} / ${Number(data.metaFondos).toLocaleString()}
                    </span>
                  )}
                </div>
                <ShareButton
                  tipo={tipo === 'urgente' ? 'urgente' : 'actividad'}
                  publicId={data.publicId}
                  title={shareTitle}
                  text={shareText}
                  data={{ ...data, esUrgente: data.esUrgente }}
                  className="pub-share"
                />
              </div>

              {data.descripcion && (
                <section className="pub-section">
                  <h2 className="pub-h2">Descripción</h2>
                  <p className="pub-p">{data.descripcion}</p>
                </section>
              )}
              {data.propositoRecaudacion && data.metaFondos > 0 && (
                <section className="pub-section">
                  <h2 className="pub-h2">Propósito de la recaudación</h2>
                  <p className="pub-p">{data.propositoRecaudacion}</p>
                </section>
              )}

              <div className="pub-actions">
                <button className="btn btn-v" onClick={() => nav('/calendario')}>
                  Ver calendario
                </button>
                <button className="btn btn-outline" onClick={() => nav('/')}>
                  Ir al inicio
                </button>
              </div>
            </div>
          </>
        )}

        {tipo === 'campana' && (
          <>
            <div className={`pub-hero pub-hero-plain ${data.urgente ? 'pub-hero-urgent' : ''}`}>
              {data.urgente && <div className="pub-urgent-banner">🚨 CAMPAÑA URGENTE</div>}
              {!data.vigente && <div className="pub-ended-banner">⏳ Campaña finalizada</div>}
              <div className="pub-kicker">Campaña</div>
              <h1 className="pub-title">{data.titulo}</h1>
              <div className="pub-chips" style={{ marginTop: 12 }}>
                <span className="pub-chip">{data.tipo || 'otro'}</span>
                <span className={`pub-chip ${data.vigente ? 'pub-chip-ok' : ''}`}>
                  {data.vigente ? '✅ Activa' : '⏸ Finalizada'}
                </span>
              </div>
            </div>
            <div className="pub-body">
              <div className="pub-head">
                <div className="pub-meta-inline">
                  <span className="pub-stat">
                    💰 ${(data.recaudado || 0).toLocaleString()} recaudados
                  </span>
                  <span className="pub-stat">meta: ${Number(data.meta || 0).toLocaleString()}</span>
                </div>
                <ShareButton
                  tipo="campana"
                  publicId={data.publicId}
                  title={shareTitle}
                  text={shareText}
                  data={data}
                  className="pub-share"
                />
              </div>

              <div className="prog-wrap mb-4">
                <div
                  className="prog-fill"
                  style={{
                    width: `${Math.min(Math.round(((data.recaudado || 0) / (data.meta || 1)) * 100), 100)}%`,
                    background: data.urgente ? 'var(--r)' : 'var(--v)'
                  }}
                />
              </div>

              {data.descripcion && (
                <section className="pub-section">
                  <h2 className="pub-h2">Acerca de esta campaña</h2>
                  <p className="pub-p">{data.descripcion}</p>
                </section>
              )}

              <div className="pub-actions">
                {data.vigente && (
                  <button className="btn btn-v" onClick={() => nav('/donaciones')}>
                    💚 Apoyar campaña
                  </button>
                )}
                <button className="btn btn-outline" onClick={() => nav('/donaciones')}>
                  Ver otras campañas
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
